<template>
  <div class="flex flex-col gap-2">
    <div class="overflow-x-auto">
      <table class="w-full min-w-[56rem] table-fixed text-sm">
        <thead>
          <tr class="bg-gray-100 dark:bg-primary-800">
            <th class="w-12 px-3 py-2 text-center uppercase">
              {{ $t('global.quantity_short') }}
            </th>
            <th class="w-full px-3 py-2 text-center uppercase">
              {{ $t('attributes.description') }}
            </th>
            <th class="w-16 px-3 py-2 text-center uppercase">
              {{ $t('global.tva') }}
            </th>
            <th class="w-24 px-3 py-2 text-center uppercase">
              {{ $t('global.unit_price_excluding_vat') }}
            </th>
            <th class="w-28 px-3 py-2 text-center uppercase">
              {{ $t('global.unit_price_tax_included') }}
            </th>
            <th class="w-28 px-3 py-2 text-center uppercase">
              {{ $t('global.total_tvac') }}
            </th>
            <th v-if="editable" class="w-28 px-3 py-2"></th>
          </tr>
        </thead>
        <draggable
          v-model="localItems"
          tag="tbody"
          item-key="id"
          drag-class="sortable-drag"
          ghost-class="sortable-ghost"
          handle=".handle"
          :disabled="!editable || busy"
          @end="saveOrder"
        >
          <template #item="{ element }">
            <CommissionNoteItemRow
              :item="element"
              :editable="editable"
              :can-see-invoice="canSeeInvoice"
              :can-see-item-settings="canSeeItemSettings"
              @edit="openEdit"
              @delete="deleteItem"
              @restore="restoreItem"
            />
          </template>
        </draggable>
      </table>
    </div>

    <CommissionNoteAddSlideOver
      v-if="addOpen"
      v-model:open="addOpen"
      :commission-note="commissionNote"
      :item="addItem"
      :initial-tab="addTab"
      @saved="emitNote"
      @close="addOpen = false"
    />
    <CommissionNoteItemDeleteModal ref="deleteModal" />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import draggable from 'vuedraggable'
import CommissionNoteAddSlideOver from '@/views/invoicing/show/commissionNote/components/CommissionNoteAddSlideOver.vue'
import CommissionNoteItemRow from '@/views/invoicing/show/commissionNote/components/CommissionNoteItemRow.vue'
import CommissionNoteItemDeleteModal from '@/views/invoicing/show/commissionNote/components/CommissionNoteItemDeleteModal.vue'
import { orderPayload } from '@/views/invoicing/show/commissionNote/components/commissionNoteItem'

/**
 * The lines of a commission note. While the note is open and the user can
 * change it (`editable`), lines can be added, edited, deleted and reordered:
 * every call answers the whole note, which is handed back to the page.
 */
export default {
  name: 'CommissionNoteItemsTable',
  components: {
    CommissionNoteAddSlideOver,
    CommissionNoteItemDeleteModal,
    CommissionNoteItemRow,
    draggable
  },
  props: {
    commissionNote: { type: Object, required: true },
    editable: { type: Boolean, default: false },
    canSeeInvoice: { type: Boolean, default: false },
    canSeeItemSettings: { type: Boolean, default: false }
  },
  emits: ['update:commission-note'],
  data() {
    return {
      localItems: [...(this.commissionNote.items ?? [])],
      busy: false,
      addOpen: false,
      addTab: 'documents',
      addItem: null
    }
  },
  watch: {
    // the note answered by the API (or reloaded) is the reference
    'commissionNote.items'(items) {
      this.localItems = [...(items ?? [])]
    }
  },
  methods: {
    ...mapActions({
      processRestoreAction: 'commissionNote/restoreCommissionNoteItem',
      processDeleteAction: 'commissionNote/deleteCommissionNoteItem',
      processReorderAction: 'commissionNote/reorderCommissionNoteItems'
    }),
    emitNote(commissionNote) {
      this.$emit('update:commission-note', commissionNote)
    },
    // the buttons live in the top bar of the page, next to the other actions
    openAdd(tab) {
      this.addTab = tab
      this.addItem = null
      this.addOpen = true
    },
    openEdit(item) {
      // a line is not edited while another request is on its way: the last answer would win
      if (this.busy) {
        return
      }

      this.addItem = item
      this.addOpen = true
    },
    // the answer replaces the note; on an error the lines stay as they were
    run(request) {
      this.busy = true

      return request
        .then((response) => {
          this.emitNote(response.commission_note)
        })
        .catch(() => {
          this.localItems = [...(this.commissionNote.items ?? [])]
        })
        .finally(() => {
          this.busy = false
        })
    },
    restoreItem(item) {
      if (this.busy) {
        return
      }

      return this.run(
        this.processRestoreAction({
          id: this.commissionNote.id,
          itemId: item.id
        })
      )
    },
    async deleteItem(item) {
      if (this.busy) {
        return
      }

      const answer = await this.$refs.deleteModal.show(item)

      if (!answer) {
        return
      }

      return this.run(
        this.processDeleteAction({
          id: this.commissionNote.id,
          itemId: item.id,
          mode: answer === true ? undefined : answer
        })
      )
    },
    saveOrder() {
      const before = orderPayload(this.commissionNote.items ?? [])
      const after = orderPayload(this.localItems)

      if (before.join() === after.join()) {
        return
      }

      return this.run(
        this.processReorderAction({
          id: this.commissionNote.id,
          items: after
        })
      )
    }
  }
}
</script>
